import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import './barcodes.scss';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button, TagBox, Tooltip, DateRangeBox, TextBox, LoadIndicator, Switch } from 'devextreme-react';
import DataGrid, { Column, KeyboardNavigation, Paging, Scrolling, Selection, Sorting } from 'devextreme-react/data-grid';
import { btnSearch, filter, ordBarcode, Pin, preferences, resButtonRefresh, UnPin } from '../../images/icons';
import { RightPanelControlled } from '../../components/utils/right-panel-controlled/RighPanel';
import { useApiBarcodes } from '../../api/api.barcodes';
import { newToast } from '../../components';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppDispatch, useAppSelector } from '../../contexts/hooks';
import { shallowEqual } from 'react-redux';
import { ConvertLocalDate, ConvertDataOrder, ConvertDataPatient } from '../../utils/functions';
import { useApiOrders } from '../../api/api.orders';
import { CustomFilters, TypeTableFilter } from '../../types/types.system';
import { OrderInfo, PatientInfo } from '../../components/library/order-patient-info';
import { BarcodeLabel } from '../../types/types.barcodes';
import { useScreenSize } from '../../utils/media-query';
import { setModalBarcodePrintersVisible } from '../../contexts/slices/config';
import { FilterComponent } from '../find-results/Modals/FilterOrders';

const Index = () => {
  //#region VARIABLES Y ESTADOS
  const { getLabelsByFilters, PrintBarcodeLabelsMasive, getPrinters } = useApiBarcodes();
  const { getHeaderOrderById, findOrder } = useApiOrders();
  const gridRef = useRef<DataGrid>(null);

  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<number[]>([]);
  const [documentFilter, setDocumentFilter] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false); // Cerrado por defecto
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentPatient, setCurrentPatient] = useState<any>(null);
  const [loadingPanel, setLoadingPanel] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [includeOrderLabel, setIncludeOrderLabel] = useState<boolean>(false);
  const { isSmall, isXSmall, isLarge, isMedium } = useScreenSize();
  const Prm_CodBarImprimir = useAppSelector((x) => x.userInfo.Permissions?.Prm_CodBarImprimir, shallowEqual) ?? false;
  const ModuleBarcode: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleBarcode')[0].Cgn_Resultado, shallowEqual) || 'false');
  const dispatch = useAppDispatch();

  // Refs para los filtros que realmente se usan en la API
  const appliedDateRangeRef = useRef<[Date, Date] | undefined>(undefined);
  const appliedAreasRef = useRef<number[]>([]);
  const appliedDocumentRef = useRef<string>('');

  // Ref para la función de cargar orden (evita problemas de closure)
  const getHeaderOrderByIdRef = useRef(getHeaderOrderById);

  // Ref para almacenar las filas seleccionadas (evita re-render al seleccionar)
  const selectedRowsRef = useRef<BarcodeLabel[]>([]);

  // Ref para almacenar los datos cargados desde la API
  const loadedDataRef = useRef<BarcodeLabel[]>([]);

  // Estado para el modal de filtros adicionales
  const [isFilterModalVisible, setIsFilterModalVisible] = useState<boolean>(false);
  const [additionalFilters, setAdditionalFilters] = useState<CustomFilters[]>([]);

  const modeEditor = useAppSelector((x) => x.configuration.IsEditing, shallowEqual);
  const Areas = useAppSelector((x) => x.configuration.Data?.Areas, shallowEqual) || [];

  // El panel está pinned (fijo) si la pantalla NO es pequeña
  const isPanelPinned = !isSmall && !isXSmall;
  //#endregion

  //#region DATASOURCE
  const customData = useMemo(() => {
    return new CustomStore({
      key: ['Ord_Id', 'Cb_Id'], // Key compuesta para unicidad
      load: () => {
        // Construir CustomFilters array para enviar a la API
        const filters: CustomFilters[] = [];

        // Agregar filtro de fecha si está definido
        if (appliedDateRangeRef.current && appliedDateRangeRef.current[0] && appliedDateRangeRef.current[1]) {
          filters.push({
            Field: 'Ord_FechaCreacion',
            TypeFilter: TypeTableFilter.Between,
            Value: ConvertLocalDate(appliedDateRangeRef.current[0]),
            Value2: ConvertLocalDate(appliedDateRangeRef.current[1]),
          });
        }

        // Agregar filtro de áreas si hay áreas seleccionadas (usando IN con array)
        if (appliedAreasRef.current && appliedAreasRef.current.length > 0) {
          filters.push({
            Field: 'Aa_Id',
            TypeFilter: TypeTableFilter.IN,
            Value: appliedAreasRef.current, // Array de IDs
          });
        }

        // Agregar filtro de documento si está definido
        if (appliedDocumentRef.current && appliedDocumentRef.current.trim() !== '') {
          filters.push({
            Field: 'Pc_Identificacion',
            TypeFilter: TypeTableFilter.Contains,
            Value: appliedDocumentRef.current.trim(),
          });
        }

        // Agregar filtros adicionales (desde el componente FilterComponent)
        if (additionalFilters.length > 0) {
          filters.push(...additionalFilters);
        }

        // Si no hay filtros, retornar vacío sin hacer llamada a la API
        if (filters.length === 0) {
          loadedDataRef.current = [];
          setTotalRecords(0);
          return Promise.resolve({
            data: [],
            totalCount: 0,
          });
        }

        return getLabelsByFilters(filters)
          .then((res) => {
            const dataArray = Array.isArray(res) ? res : [];
            loadedDataRef.current = dataArray;
            setTotalRecords(dataArray.length);

            // Si solo hay un Pc_Id único, cargar el paciente automáticamente
            if (dataArray.length > 0) {
              const uniquePcIds = Array.from(new Set(dataArray.map((item) => item.Pc_Id)));
              if (uniquePcIds.length === 1 && uniquePcIds[0]) {
                // Cargar el primer registro para obtener la información completa
                const firstOrdId = dataArray[0].Ord_Id;
                getHeaderOrderByIdRef
                  .current(firstOrdId)
                  .then((orderRes) => {
                    if (orderRes) {
                      const { Orden, Paciente } = orderRes;
                      const convertedData = ConvertDataPatient(Paciente);
                      const convertedOrder = ConvertDataOrder(Orden);
                      setCurrentOrder(convertedOrder);
                      setCurrentPatient(convertedData);
                    }
                  })
                  .catch((error) => {
                    console.error('Error al cargar paciente único:', error);
                  });
              }
            }

            return {
              data: dataArray,
              totalCount: dataArray.length,
            };
          })
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
    });
  }, [additionalFilters]); // Sin dependencias - el store nunca se recrea

  const customDataSrc = useMemo(() => {
    return new DataSource({
      store: customData,
      reshapeOnPush: false,
    });
  }, [customData]);
  //#endregion

  //#region EFFECTS
  useEffect(() => {
    // Actualizar ref de la función
    getHeaderOrderByIdRef.current = getHeaderOrderById;
  }, [getHeaderOrderById]);

  useEffect(() => {
    // NO cargar datos iniciales - el usuario debe aplicar filtros
    // Los refs se inicializan vacíos
    appliedDateRangeRef.current = undefined;
    appliedAreasRef.current = [];
    appliedDocumentRef.current = '';
  }, []);

  useEffect(() => {
    // Solo refrescar cuando refreshTrigger cambie (cuando se aplique filtro)
    if (refreshTrigger > 0) {
      gridRef.current?.instance.refresh();
    }
  }, [refreshTrigger]);
  //#endregion

  //#region HANDLERS
  const handleKeys = useCallback((event: any) => {
    if (event.event?.key === 'F3') {
      // Aquí puedes agregar la funcionalidad que necesites con F3
      event.event.preventDefault();
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    // Validar que al menos un filtro esté definido
    const hasDateRange = dateRange && dateRange[0] && dateRange[1];
    const hasAreas = selectedAreas && selectedAreas.length > 0;
    const hasDocument = documentFilter && documentFilter.trim() !== '';

    if (!hasDateRange && !hasAreas && !hasDocument) {
      newToast('WARNING', 'Por favor defina al menos un filtro antes de aplicar', 3);
      return;
    }

    // Actualizar los refs con los nuevos valores
    appliedDateRangeRef.current = hasDateRange ? [dateRange[0], dateRange[1]] : undefined;
    appliedAreasRef.current = selectedAreas || [];
    appliedDocumentRef.current = documentFilter || '';

    // Incrementar el trigger para disparar el refresh
    setRefreshTrigger((prev) => prev + 1);
  }, [dateRange, selectedAreas, documentFilter]);

  const handleDateRangeChange = useCallback((e: any) => {
    const value = e.value;
    // Simplemente actualizar el estado con lo que venga del componente
    setDateRange(value || null);
  }, []);

  const handleAreasChange = useCallback((e: any) => {
    setSelectedAreas(e.value || []);
  }, []);

  const handleDocumentChange = useCallback(
    (e: any) => {
      const newValue = e.value || '';
      setDocumentFilter(newValue);

      // Si hay valor, ejecutar búsqueda inmediatamente (se dispara con Enter)
      if (newValue.trim()) {
        // Usar el nuevo valor directamente en lugar de esperar el estado
        const hasDateRange = dateRange && dateRange[0] && dateRange[1];
        const hasAreas = selectedAreas && selectedAreas.length > 0;

        // Actualizar los refs con los nuevos valores
        appliedDateRangeRef.current = hasDateRange ? [dateRange[0], dateRange[1]] : undefined;
        appliedAreasRef.current = selectedAreas || [];
        appliedDocumentRef.current = newValue.trim();

        // Incrementar el trigger para disparar el refresh
        setRefreshTrigger((prev) => prev + 1);
      }
    },
    [dateRange, selectedAreas]
  );

  // Handler para aplicar filtros adicionales desde el componente FilterComponent
  const handleApplyAdditionalFilters = useCallback(
    (filters: CustomFilters[]) => {
      console.log('Barcodes - Filtros recibidos desde FilterComponent:', filters);
      setAdditionalFilters(filters);
      setIsFilterModalVisible(false);
      // Forzar refresh del grid
      setRefreshTrigger((prev) => prev + 1);
    },
    [setRefreshTrigger]
  );

  // Handler para abrir el modal de filtros adicionales
  const handleOpenFilterModal = useCallback(() => {
    setIsFilterModalVisible(true);
  }, []);

  // Handler para selección de filas - almacena en ref sin causar re-render
  const handleSelectionChanged = useCallback((e: any) => {
    selectedRowsRef.current = e.selectedRowsData || [];
  }, []);

  // Handler para cargar datos de la orden al hacer clic en la columna ORDEN
  const handleOrderClick = useCallback(
    async (orderId: number) => {
      // Solo abrir el panel en pantallas pequeñas, en pantallas grandes ya está abierto
      if (!isPanelPinned) {
        setIsPanelOpen(true);
      }
      setLoadingPanel(true);

      try {
        const res = await getHeaderOrderById(orderId);

        if (res) {
          const { Orden, Paciente } = res;
          const convertedData = ConvertDataPatient(Paciente);
          const convertedOrder = ConvertDataOrder(Orden);

          setCurrentOrder(convertedOrder);
          setCurrentPatient(convertedData);
        } else {
          setCurrentOrder(null);
          setCurrentPatient(null);
          newToast('WARNING', 'No se encontró información de la orden', 3);
        }
      } catch (error) {
        console.error('Error al cargar orden:', error);
        newToast('ERROR', 'Error al cargar los datos de la orden', 3);
        setCurrentOrder(null);
        setCurrentPatient(null);
      } finally {
        setLoadingPanel(false);
      }
    },
    [getHeaderOrderById, isPanelPinned]
  );

  // Handler para cerrar el panel y limpiar datos
  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setCurrentOrder(null);
    setCurrentPatient(null);
  }, []);

  // Handler para alternar la visibilidad del panel (mostrar/ocultar)
  const handleTogglePanel = useCallback(() => {
    if (isPanelPinned) {
      // En pantallas grandes el panel siempre está visible, no hacer nada
      return;
    }
    // En pantallas pequeñas/medianas, alternar visibilidad
    setIsPanelOpen((prev) => !prev);
  }, [isPanelPinned]);

  // Handler para ir a la orden
  const handleClickViewOrder = useCallback(() => {
    if (currentOrder) {
      findOrder(undefined, currentOrder.Ord_Id, setLoading);
    }
  }, [currentOrder, findOrder]);

  // Handler para imprimir etiquetas de códigos de barras
  const handlePrintBarcode = useCallback(async () => {
    const selectedRows = selectedRowsRef.current;

    if (!selectedRows || selectedRows.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos una etiqueta para imprimir', 4);
      return;
    }

    // Verificar si tenemos una impresora configurada en localStorage
    const printerData = localStorage.getItem('defaultPrinter');
    let printer: any;

    if (printerData) {
      printer = JSON.parse(printerData);
    } else {
      const printers = await getPrinters();
      if (!printers || printers.length === 0) {
        newToast('WARNING', 'No hay impresoras configuradas', 4);
        return;
      } else if (printers.length === 1) {
        // Seleccionar la única impresora como predeterminada
        localStorage.setItem('defaultPrinter', JSON.stringify(printers[0]));
        printer = printers[0];
        newToast('INFO', `Se ha establecido la impresora '${printer.Cbp_Nombre}' como predeterminada.`, 5);
      } else {
        // Mostrar modal de varias impresoras
        dispatch(setModalBarcodePrintersVisible(true));
        return;
      }
    }

    // Agrupar por Ord_Id y Cb_Id
    const groupedData = selectedRows.reduce((acc, row) => {
      const key = `${row.Ord_Id}_${row.Cb_Id}`;
      if (!acc[key]) {
        acc[key] = {
          Ord_Id: row.Ord_Id,
          Cb_Id: row.Cb_Id,
          labels: [],
        };
      }
      acc[key].labels.push(row);
      return acc;
    }, {} as Record<string, { Ord_Id: number; Cb_Id: number; labels: BarcodeLabel[] }>);

    // Crear el array de OrderSamplesRequest
    const orderSamplesRequests = Object.values(groupedData).map((group) => ({
      IdOrden: group.Ord_Id,
      LstIdsMuestras: [group.Cb_Id],
      IdPrinter: printer.Cbp_Id,
      includeOrderLabel: includeOrderLabel,
    }));
   

    // Llamar al método de impresión masiva
    await PrintBarcodeLabelsMasive(orderSamplesRequests, printer);
  }, [getPrinters, PrintBarcodeLabelsMasive, dispatch]);

  // Render personalizado para la columna ORDEN con clic
  const renderOrderCell = useCallback(
    (cellData) => {
      return (
        <div style={{ cursor: 'pointer', color: '#337ab7', textDecoration: 'underline' }} onClick={() => handleOrderClick(cellData.data.Ord_Id)}>
          {cellData.data.Ord_Numero}
        </div>
      );
    },
    [handleOrderClick]
  );

  // Render personalizado para el estado de procesado
  const renderProcessedStatus = useCallback((cellData) => {
    const isProcessed = cellData.data.Procesado === 1;
    const backgroundColor = isProcessed ? '#49A54A' : '#F6C024';
    const text = isProcessed ? 'Procesado' : 'Pendiente';

    return (
      <div
        style={{
          display: 'inline-block',
          padding: '1px 7px',
          borderRadius: '12px',
          backgroundColor: backgroundColor,
          color: '#fff',
          fontSize: '10px',
          fontWeight: '500',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}>
        {text}
      </div>
    );
  }, []);

  // Render personalizado para el estado de impresión
  const renderPrintStatus = useCallback((cellData) => {
    const isPrinted = cellData.data.EstadoImpresion === 1;
    const backgroundColor = isPrinted ? '#4C97F1' : '#F6C024';
    const text = isPrinted ? 'Impresa' : 'Pendiente';

    return (
      <div
        style={{
          display: 'inline-block',
          padding: '1px 7px',
          borderRadius: '12px',
          backgroundColor: backgroundColor,
          color: '#fff',
          fontSize: '10px',
          fontWeight: '500',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          cursor: isPrinted ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (isPrinted) {
            // TODO: Implementar funcionalidad de clic para estado impreso
            console.log('Clic en estado impreso para orden:', cellData.data.Ord_Id);
          }
        }}>
        {text}
      </div>
    );
  }, []);
  //#endregion

  const handleClickChoosePrinter = useCallback(() => {
    dispatch(setModalBarcodePrintersVisible(true));
  }, [dispatch]);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper-contact-list'>
        <div className='view-wrapper-mass-validation' style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div className='toolbar-mass-validation'>
            <Toolbar>
              <Item location='before' visible={isLarge || isMedium}>
                <div className='grid-header'>Códigos de barras</div>
              </Item>
              <Item location='before' locateInMenu='auto' visible={isLarge || isMedium}>
                <div className='separator' />
              </Item>
              <Item location='before' locateInMenu='auto'>
                <DateRangeBox {...(dateRange ? { value: dateRange } : {})} onValueChanged={handleDateRangeChange} displayFormat='dd/MM/yyyy' width={220} stylingMode='outlined' startDateLabel='Fecha Inicio' endDateLabel='Fecha Fin' showClearButton={true} />
              </Item>
              <Item location='before' locateInMenu='auto'>
                <TagBox value={selectedAreas} items={Areas} displayExpr='Aa_Nombre' valueExpr='Aa_Id' labelMode='static' label='Áreas' width={200} onValueChanged={handleAreasChange} showSelectionControls={true} applyValueMode='useButtons' stylingMode='outlined' showClearButton={true} maxDisplayedTags={1} height={32} dropDownOptions={{ width: 260 }} />
              </Item>
              <Item location='before' locateInMenu='auto'>
                <TextBox value={documentFilter} onValueChanged={handleDocumentChange} labelMode='static' label='Documento' width={200} stylingMode='outlined' placeholder='Buscar por documento...' showClearButton={true} />
              </Item>
              <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button className='filter-button' icon={btnSearch} id='AplicarFiltros' style={{ marginTop: 7 }} text='Buscar' stylingMode='text' onClick={handleApplyFilters} />
                <Tooltip target={'#AplicarFiltros'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Buscar etiquetas
                </Tooltip>
              </Item>

              <Item location='after' locateInMenu='auto' visible={isLarge || isMedium}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px', minWidth: 'fit-content' }}>
                  <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                    Etiquetas: <strong>{totalRecords}</strong>
                  </span>
                </div>
              </Item>

              <Item location='after' visible={Prm_CodBarImprimir && ModuleBarcode} widget='dxButton' showText='inMenu' locateInMenu='never'>
                <Button icon={ordBarcode} id='OrdPrintBarcode' stylingMode='text' text='Imprimir etiquetas' onClick={handlePrintBarcode} />
                <Tooltip target={'#OrdPrintBarcode'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Imprimir etiquetas seleccionadas
                </Tooltip>
              </Item>

              <Item location='after' visible={Prm_CodBarImprimir && ModuleBarcode} locateInMenu='auto'>
                <div id='SwitchIncludeOrderLabel' style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px' }}>
                  <Switch value={includeOrderLabel} onValueChange={setIncludeOrderLabel} />
                  <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>Imp. Etiqueta orden</span>
                </div>
                <Tooltip target={'#SwitchIncludeOrderLabel'} showEvent='dxhoverstart' hideEvent='dxhoverend' position={'top'}>
                  Imprimir también las etiquetas para marcar la orden
                </Tooltip>
              </Item>

              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>

              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='AplicarFiltros2' icon={filter} text='Filtros adicionales' stylingMode='text' onClick={handleOpenFilterModal} />
                <Tooltip target={'#AplicarFiltros2'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Filtros adicionalesF
                </Tooltip>
              </Item>
              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='RefrescarMasivo' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                <Tooltip target={'#RefrescarMasivo'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Refrescar datos
                </Tooltip>
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='Preferences' icon={preferences} text='Impresora predeterminada' stylingMode='text' onClick={handleClickChoosePrinter} />
                <Tooltip target={'#Preferences'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Configurar impresora predeterminada
                </Tooltip>
              </Item>
              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>
              <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton' visible={!isPanelPinned}>
                <Button 
                  id='showpanel' 
                  icon={isPanelOpen ? Pin : UnPin} 
                  text={isPanelOpen ? 'Panel Visible' : 'Panel oculto'} 
                  stylingMode='text' 
                  onClick={handleTogglePanel} 
                />
                <Tooltip target={'#showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  {isPanelOpen ? 'Ocultar panel' : 'Mostrar panel'}
                </Tooltip>
              </Item>
            </Toolbar>
          </div>

          <div id='gridContainer' style={{ flex: 1, overflow: 'auto', paddingBottom: '40px', width: '100%' }}>
            <DataGrid onKeyDown={handleKeys} onSelectionChanged={handleSelectionChanged} hoverStateEnabled className='gridResult' height={'100%'} dataSource={customDataSrc} ref={gridRef} width={'100%'} repaintChangesOnly={false} highlightChanges={false} rowAlternationEnabled={false} showColumnLines={true} cacheEnabled={false}>
              <Selection mode='multiple' selectAllMode='page' showCheckBoxesMode='always' />
              <Scrolling mode='standard' showScrollbar='always' useNative={true} scrollByContent={true} columnRenderingMode='virtual' />
              <Sorting mode='multiple' />
              <KeyboardNavigation editOnKeyPress={false} enterKeyAction='moveFocus' enterKeyDirection='row' />
              <Paging enabled={false} />
              <Column allowGrouping={false} allowSorting={false} caption='ID ORDEN' allowEditing={false} visible={modeEditor} dataField='Ord_Id' width={75} alignment='center' />
              <Column allowGrouping={false} allowSorting={true} caption='ORDEN' showInColumnChooser={false} allowEditing={false} dataField='Ord_Numero' width={100} alignment='center' cellRender={renderOrderCell} />
              <Column allowGrouping={false} allowSorting={true} caption='CÓDIGO BARRAS' showInColumnChooser={false} allowEditing={false} dataField='Cb_Nombre' width={150} alignment='center' />
              <Column allowGrouping={false} allowSorting={true} caption='TIPO MUESTRA' allowEditing={false} dataField='Tm_Nombre' alignment='center' width={120} visible={isLarge || isMedium} />
              <Column allowGrouping={false} allowSorting={true} caption='ESTUDIOS' minWidth={200} showInColumnChooser={false} allowEditing={false} dataField='Estudios' visible={isLarge || isMedium} />
              <Column allowGrouping={false} allowSorting={true} caption='FECHA' allowEditing={false} dataField='Ord_FechaCreacion' alignment='center' width={100} dataType='date' format='dd/MM/yyyy' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={false} width={110} alignment='center' cellRender={renderPrintStatus} />
              <Column allowGrouping={false} allowSorting={false} caption='PROCESADO' allowEditing={false} width={110} alignment='center' cellRender={renderProcessedStatus} visible={isLarge || isMedium} />
            </DataGrid>
          </div>
        </div>
        <RightPanelControlled isVisible={isPanelPinned || isPanelOpen} isPinned={isPanelPinned} onClose={handleClosePanel} includePin={true} title='Consulta de Orden'>
          {loadingPanel ? (
            <div style={{ padding: '30px', textAlign: 'center' }}>
              <LoadIndicator visible={true} />
              <div style={{ marginTop: '15px', color: '#666' }}>Cargando información...</div>
            </div>
          ) : currentOrder ? (
            <>
              <OrderInfo orderData={currentOrder} />
              <PatientInfo patientData={currentPatient} />
              <div style={{ padding: '15px', borderTop: '1px solid #e0e0e0' }}>
                <Button text='IR A LA ORDEN' type='default' stylingMode='contained' width='100%' onClick={handleClickViewOrder} disabled={loading} />
              </div>
            </>
          ) : (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
              <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                <i className='dx-icon-info' style={{ fontSize: '48px', color: '#ddd' }} />
              </div>
              <div>Selecciona una orden para ver sus detalles</div>
            </div>
          )}
        </RightPanelControlled>

        {/* Modal de filtros adicionales */}
        <FilterComponent visible={isFilterModalVisible} setVisible={setIsFilterModalVisible} onApplyFilters={handleApplyAdditionalFilters} includeOrderFields={true} includePatientFields={false} />
      </div>
    </div>
  );
};

export default Index;
